import {useEffect, useRef, useState} from "react";

import {ActionDialog} from "@/components/overlay/ActionDialog.tsx";
import {NetworkRunTestResult} from "@/lib/types/network.ts";

import {NetworkProbeModal} from "./NetworkProbeModal.tsx";
import {RunState} from "./useNetworkRunTest.ts";

interface NetworkAnalysisRunTestModalProps {
  state: RunState;
  result: NetworkRunTestResult | null;
  error: string | null;
  title: string;
}

export const NetworkAnalysisRunTestModal = ({state, result, error, title}: NetworkAnalysisRunTestModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const previousState = useRef(state);

  // Open only on the transition out of "running", so a result that already exists
  // (e.g. the window was narrowed after a run) does not pop the modal up.
  useEffect(() => {
    const isFinished = state === "completed" || state === "failed";
    if (previousState.current === "running" && isFinished) setIsOpen(true);
    if (state === "running") setIsOpen(false);
    previousState.current = state;
  }, [state]);

  if (!isOpen) return null;

  const probes = !error && result ? result.probes : [];
  const onClose = () => setIsOpen(false);

  if (probes.length > 0) {
    return <NetworkProbeModal show title={title} probes={probes} context="run-test" onClose={onClose} />;
  }

  const message = error ?? result?.summary ?? "";

  return (
    <ActionDialog
      show
      title={title}
      titleClassName="pulseTextH2Bold"
      onCancel={onClose}
      onConfirm={onClose}
      confirmBtnText="Close"
      hasCancelButton={false}
    >
      <p className={error ? "text-danger mb-0" : "mb-0"}>{message}</p>
    </ActionDialog>
  );
};
